import React, { useCallback, useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress } from "@mui/material";
import {
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";

import UnifiedDashboard from "./pages/UnifiedDashboard";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import NewDevice from "./pages/NewDevice";
import TermsAndConditions from "./pages/TermsAndConditions";
import Footer from "./components/Layout/Footer";

import theme from "./theme/theme";
import "./App.css";

import { RegionalProvider } from "./components/Regional/RegionalContext";
import {
    TranslationProvider,
    useTranslate,
} from "./components/Translate/TranslateContext";

import { authUser } from "./api/auth";
import { userLogout } from "./api/logout";

function normalizeLoginUser(loginPayload) {
    const loginUser = loginPayload?.user || loginPayload;

    if (!loginUser) return null;

    return {
        ...loginUser,
        needs_terms_acceptance: Boolean(loginPayload?.needs_terms_acceptance),
        preferences: loginUser.preferences || {},
    };
}

function LoadingScreen() {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--color-bg-dark)",
            }}
        >
            <CircularProgress />
        </Box>
    );
}

function AppRoutes({ user, setUser, loading, onLogout }) {
    const { translate } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();

    const needsTermsAcceptance = Boolean(user?.needs_terms_acceptance);

    useEffect(() => {
        document.title = translate("Farmsync");
    }, [translate]);

    useEffect(() => {
        if (loading) return;
        if (!user) return;
        if (!needsTermsAcceptance) return;
        if (location.pathname === "/terms") return;

        navigate("/terms", { replace: true });
    }, [
        loading,
        user,
        needsTermsAcceptance,
        location.pathname,
        navigate,
    ]);

    const handleLoginSuccess = useCallback(
        (loginPayload) => {
            const nextUser = normalizeLoginUser(loginPayload);
            if (!nextUser) return;

            setUser(nextUser);

            if (nextUser.needs_terms_acceptance) {
                navigate("/terms", { replace: true });
                return;
            }

            navigate("/", { replace: true });
        },
        [setUser, navigate]
    );

    const handleTermsAccepted = useCallback(() => {
        setUser((currentUser) => {
            if (!currentUser) return currentUser;

            return {
                ...currentUser,
                needs_terms_acceptance: false,
            };
        });

        navigate("/", { replace: true });
    }, [setUser, navigate]);

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <div
            className="App"
            style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                backgroundColor: user
                    ? "var(--color-green-strong)"
                    : "var(--color-bg-dark)",
                transition: "background-color 0.3s ease",
            }}
        >
            <Box component="main" sx={{ flex: 1 }}>
                <Routes>
                    <Route
                        path="/terms"
                        element={
                            <TermsAndConditions
                                user={user}
                                onTermsAccepted={handleTermsAccepted}
                            />
                        }
                    />

                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/new-device" element={<NewDevice />} />

                    <Route
                        path="/"
                        element={
                            user ? (
                                needsTermsAcceptance ? (
                                    <Navigate to="/terms" replace />
                                ) : (
                                    <UnifiedDashboard
                                        user={user}
                                        onLogout={onLogout}
                                        setUser={setUser}
                                    />
                                )
                            ) : (
                                <Login onLoginSuccess={handleLoginSuccess} />
                            )
                        }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Box>

            <Footer />
        </div>
    );
}

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const handleLogout = useCallback(async () => {
        try {
            await userLogout();
        } finally {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        const loadUser = async () => {
            try {
                const data = await authUser();

                if (!mounted) return;

                if (data?.success && data.user) {
                    setUser(normalizeLoginUser(data));
                } else {
                    setUser(null);
                }
            } catch {
                if (mounted) {
                    setUser(null);
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadUser();

        return () => {
            mounted = false;
        };
    }, []);

    /*
    useEffect(() => {
        if (!user?.user_id) return;

        const apiBase = process.env.REACT_APP_API_BASE_FULL || "";

        if (!apiBase) return;

        const wsPath =
            apiBase
                .replace(/\/$/, "")
                .replace(/^http:\/\//, "ws://")
                .replace(/^https:\/\//, "wss://") + "/api/wsListener";

        let ws;
        let heartbeat;
        let reconnectTimeout;
        let closedManually = false;

        const connect = () => {
            ws = new WebSocket(wsPath);

            ws.onopen = () => {
                ws.send(JSON.stringify({ user_id: user.user_id }));

                heartbeat = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ type: "PING" }));
                    }
                }, 30000);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === "LOGOUT") {
                        closedManually = true;
                        handleLogout();
                    }
                } catch {}
            };

            ws.onerror = () => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.close();
                }
            };

            ws.onclose = (event) => {
                clearInterval(heartbeat);

                if (!closedManually && !event.wasClean) {
                    const delay = 3000 + Math.random() * 2000;
                    reconnectTimeout = setTimeout(connect, delay);
                }
            };
        };

        connect();

        return () => {
            closedManually = true;
            clearInterval(heartbeat);
            clearTimeout(reconnectTimeout);

            if (
                ws?.readyState === WebSocket.OPEN ||
                ws?.readyState === WebSocket.CONNECTING
            ) {
                ws.close();
            }
        };
    }, [user, handleLogout]);
    */

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <RegionalProvider user={user}>
                <TranslationProvider>
                    <AppRoutes
                        user={user}
                        setUser={setUser}
                        loading={loading}
                        onLogout={handleLogout}
                    />
                </TranslationProvider>
            </RegionalProvider>
        </ThemeProvider>
    );
}

export default App;
